/**
 * Search Revamp - Enhanced Search Functionality
 * Handles predictive search, suggestions, and recommended products
 */
(function() {
  'use strict';

  // Configuration - will be set from data attributes
  const config = {
    routesPredictiveSearchURL: '',
    searchShowProductsOnly: false,
    fallbackImage: ''
  };

  // State management
  let debounceTimer = null;
  let currentAbortController = null;
  let currentProductsAbortController = null;
  let cachedRandomProducts = null;
  let isRenderingProducts = false;
  let loadProductsTimeout = null;

  // DOM elements
  let searchInput, searchSuggestions, clearButton, searchForm;
  let recommendedProductsGrid, recommendedProductsContainer;

  /**
   * Initialize configuration from data attributes or global variables
   */
  function initConfig() {
    const searchContainer = document.querySelector('.search-sidebar');
    if (searchContainer) {
      const dataRoutes = searchContainer.dataset.routesPredictiveSearchUrl;
      const dataProductsOnly = searchContainer.dataset.searchShowProductsOnly;
      
      if (dataRoutes) {
        config.routesPredictiveSearchURL = dataRoutes;
      } else if (window.Shopify && window.Shopify.routes) {
        config.routesPredictiveSearchURL = window.Shopify.routes.predictive_search_url || '/search/suggest';
      } else {
        config.routesPredictiveSearchURL = '/search/suggest';
      }

      if (dataProductsOnly !== undefined) {
        config.searchShowProductsOnly = dataProductsOnly === 'true';
      }
    }

    const dataFallbackImage = document.querySelector('[data-fallback-image]');
    if (dataFallbackImage) {
      config.fallbackImage = dataFallbackImage.dataset.fallbackImage;
    }
  }

  /**
   * Toggle clear button visibility based on input value
   */
  function toggleClearButton() {
    if (!searchInput || !clearButton) return;

    if (searchInput.value.trim() !== '') {
      clearButton.classList.add('show');
    } else {
      clearButton.classList.remove('show');
    }
  }

  /**
   * Initialize DOM references
   */
  function initDOMElements() {
    searchInput = document.getElementById('searchbox');
    searchSuggestions = document.getElementById('search-suggestions');
    clearButton = document.getElementById('search-clear-btn');
    searchForm = document.querySelector('.search-form');
    recommendedProductsGrid = document.getElementById('recommended-products-grid');
    recommendedProductsContainer = document.querySelector('.recommended-products__container');

    if (!searchInput || !searchSuggestions || !searchForm) {
      return false;
    }

    // Initialize clear button visibility
    if (clearButton) {
      toggleClearButton();
    }

    // Hide recommended products grid initially
    if (recommendedProductsGrid) {
      recommendedProductsGrid.style.display = 'none';
    }

    return true;
  }

  /**
   * Debounce utility function
   */
  function debounce(func, wait) {
    return function(...args) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Show/hide suggestions
   */
  function showSuggestions() {
    if (searchInput.value.trim() && searchSuggestions.innerHTML.trim()) {
      searchSuggestions.classList.add('show');
    }
  }

  function hideSuggestions() {
    searchSuggestions.classList.remove('show');
  }

  /**
   * Clear search input and suggestions
   */
  function clearSearch() {
    searchInput.value = '';
    searchSuggestions.innerHTML = '';
    hideSuggestions();
    toggleClearButton();
    searchInput.focus();
    if (currentAbortController) {
      currentAbortController.abort();
    }
  }

  /**
   * Extract suggestions from JSON response
   */
  function extractSuggestionsFromJSON(jsonData, query) {
    const items = [];
    const queryLower = query.toLowerCase();
    const excludedCollectionHandle = 'mothers-day-2024-exclusions';

    // Extract query suggestions from Search & Discovery
    if (jsonData?.queries && Array.isArray(jsonData.queries)) {
      jsonData.queries.forEach(q => {
        const suggestion = typeof q === 'string' ? q : (q?.text || q?.query || '');
        if (suggestion && suggestion.trim()) {
          items.push(suggestion.trim());
        }
      });
    }

    // Extract collection/page/article titles
    if (jsonData?.resources?.results) {
      const collections = jsonData.resources.results.collections || [];
      collections.forEach(c => {
        if (c?.title && c?.handle !== excludedCollectionHandle) {
          items.push(c.title);
        }
      });

      const pages = jsonData.resources.results.pages || [];
      pages.forEach(p => {
        if (p?.title) items.push(p.title);
      });

      const articles = jsonData.resources.results.articles || [];
      articles.forEach(a => {
        if (a?.title) items.push(a.title);
      });

      // Generate suggestions from product titles if no的了 suggestions
      if (items.length === 0 && jsonData.resources.results.products) {
        const suggestions = generateSuggestionsFromProducts(jsonData.resources.results.products, query, queryLower);
        items.push(...suggestions);
      }
    }

    return items;
  }

  /**
   * Generate search suggestions from product titles
   */
  function generateSuggestionsFromProducts(products, query, queryLower) {
    const suggestions = new Set();

    products.forEach(product => {
      if (!product?.title) return;

      const title = product.title.trim();
      const titleLower = title.toLowerCase();

      if (!titleLower.includes(queryLower)) return;

      // Add shorter product titles
      if (title.length <= 30 && title.length > query.length) {
        suggestions.add(title);
      }

      // Extract meaningful phrases
      const words = titleLower.split(/\s+/);

      words.forEach((word, index) => {
        if (word.includes(queryLower)) {
          if (word.length > queryLower.length) {
            suggestions.add(word);
          }

          // 2-word phrases after query word
          if (index < words.length - 1) {
            const phrase2 = `${word} ${words[index + 1]}`;
            if (phrase2.length <= 30 && phrase2.length > query.length) {
              suggestions.add(phrase2);
            }
          }

          // 2-word phrases before query word
          if (index > 0) {
            const phrase2 = `${words[index - 1]} ${word}`;
            if (phrase2.length <= 30 && phrase2.length > query.length) {
              suggestions.add(phrase2);
            }
          }

          // 3-word phrases
          if (index < words.length - 2) {
            const phrase3 = `${word} ${words[index + 1]} ${words[index + 2]}`;
            if (phrase3.length <= 35 && phrase3.length > query.length) {
              suggestions.add(phrase3);
            }
          }
        }
      });
    });

    // Sort and limit suggestions
    return Array.from(suggestions)
      .filter(s => s.length >= query.length)
      .sort((a, b) => {
        if (a.length !== b.length) return a.length - b.length;
        return a.localeCompare(b);
      })
      .slice(0, 5);
  }

  /**
   * Extract suggestions from HTML response
   */
  function extractSuggestionsFromHTML(html) {
    const items = [];
    if (!html) return items;

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');

      if (section) {
        const links = section.querySelectorAll('a.predictive-search__item');
        links.forEach(link => {
          if (link.closest('.product-index')) return;

          const text = link.textContent.trim();
          if (text &&
              text.length > 0 &&
              !text.toLowerCase().includes('search for') &&
              !text.includes('$') &&
              !text.includes('Add to Bag')) {
            items.push(text);
          }
        });
      }
    } catch (e) {
      console.debug('Error parsing HTML for suggestions:', e);
    }

    return items;
  }

  /**
   * Fetch search suggestions
   */
  function fetchSearchSuggestions(query) {
    // Cancel pending requests
    if (currentProductsAbortController) {
      currentProductsAbortController.abort();
    }

    if (currentAbortController) {
      currentAbortController.abort();
    }

    currentAbortController = new AbortController();
    currentProductsAbortController = new AbortController();

    if (!query || query.trim().length < 2) {
      searchSuggestions.innerHTML = '';
      hideSuggestions();
      if (!isRenderingProducts) {
        renderRecommendedProducts([]);
      }
      return;
    }

    const resourceTypes = config.searchShowProductsOnly
      ? 'product'
      : 'product,collection,article,page';

    const jsonUrl = `${config.routesPredictiveSearchURL}.json?q=${encodeURIComponent(query)}&resources[type]=${encodeURIComponent(resourceTypes)}&resources[limit]=6`;
    const htmlUrl = `${config.routesPredictiveSearchURL}?q=${encodeURIComponent(query)}&resources[type]=${encodeURIComponent(resourceTypes)}&resources[limit]=6&section_id=predictive-search`;

    Promise.all([
      fetch(jsonUrl, {
        signal: currentAbortController.signal,
        headers: { 'Accept': 'application/json' }
      })
        .then(res => res.ok ? res.json() : null)
        .catch(() => null),
      fetch(htmlUrl, { signal: currentProductsAbortController.signal })
        .then(res => res.ok ? res.text() : null)
        .catch(() => null)
    ])
    .then(([jsonData, html]) => {
      let items = [];

      // Extract from JSON first
      if (jsonData) {
        items = extractSuggestionsFromJSON(jsonData, query);
      }

      // Fall back to HTML if no JSON suggestions
      if (items.length === 0 && html) {
        items = extractSuggestionsFromHTML(html);
      }

      // Remove duplicates and limit
      const unique = Array.from(new Set(items.filter(Boolean)));
      const finalSuggestions = unique.length > 0 ? unique.slice(0, 5) : [];

      renderSuggestionsList(finalSuggestions);

      // Extract products from HTML
      if (html && !currentProductsAbortController.signal.aborted) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');

        if (section) {
          fetchRecommendedProductsFromHTML(query, section);
        } else if (!isRenderingProducts) {
          if (cachedRandomProducts && cachedRandomProducts.length > 0) {
            renderRecommendedProducts(cachedRandomProducts);
          } else {
            renderRecommendedProducts([]);
          }
        }
      } else if (!html && !currentProductsAbortController.signal.aborted && !isRenderingProducts) {
        if (cachedRandomProducts && cachedRandomProducts.length > 0) {
          renderRecommendedProducts(cachedRandomProducts);
        } else {
          renderRecommendedProducts([]);
        }
      }
    })
    .catch(err => {
      if (err.name !== 'AbortError') {
        searchSuggestions.innerHTML = '';
        hideSuggestions();
        if (!isRenderingProducts) {
          renderRecommendedProducts([]);
        }
      }
    });
  }

  /**
   * Render suggestions list
   */
  function renderSuggestionsList(suggestions) {
    if (!suggestions || !suggestions.length) {
      searchSuggestions.innerHTML = '';
      hideSuggestions();
      return;
    }

    const html = suggestions.map(txt => {
      const val = String(txt).trim();
      const dataAttr = val.replace(/"/g, '&quot;');
      const display = val.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<div class="suggestion-item" data-suggestion="${dataAttr}">${display}</div>`;
    }).join('');

    searchSuggestions.innerHTML = html;

    // Attach click handlers
    const suggestionItems = searchSuggestions.querySelectorAll('.suggestion-item');
    suggestionItems.forEach(item => {
      item.addEventListener('click', function() {
        const suggestionText = this.dataset.suggestion;
        searchInput.value = suggestionText;
        hideSuggestions();
        searchForm.submit();
      });
    });

    showSuggestions();
  }

  /**
   * Extract product data from HTML section
   */
  function extractProductsFromHTML(section) {
    const products = [];
    const productLinks = section.querySelectorAll('a[href*="/products/"], .product-index a[href*="/products/"]');

    productLinks.forEach(link => {
      const url = link.getAttribute('href');
      if (!url) return;

      // Find title
      let title = '';
      const titleEl = link.querySelector('h3, h2, h4, .product-title, [class*="title"], [class*="name"]');
      if (titleEl) {
        title = titleEl.textContent.trim();
      } else {
        title = link.textContent.trim();
      }

      // Find image
      let imageUrl = '';
      const productItem = link.closest('.product-index');
      if (productItem) {
        const imgContainer = productItem.querySelector('.js-product-image, .reveal, .prod-image');
        if (imgContainer) {
          const imgEl = imgContainer.querySelector('img');
          if (imgEl) {
            const srcset = imgEl.getAttribute('srcset');
            if (srcset) {
              const srcsetParts = srcset.split(',').map(s => s.trim());
              if (srcsetParts.length > 0) {
                imageUrl = srcsetParts[srcsetParts.length - 1].split(' ')[0];
              }
            }
            if (!imageUrl) {
              imageUrl = imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || '';
            }
          }
        }

        // Fallback: find any img in product item
        if (!imageUrl) {
          const imgEl = productItem.querySelector('img');
          if (imgEl) {
            const srcset = imgEl.getAttribute('srcset');
            if (srcset) {
              const srcsetParts = srcset.split(',').map(s => s.trim());
              if (srcsetParts.length > 0) {
                imageUrl = srcsetParts[srcsetParts.length - 1].split(' ')[0];
              }
            }
            if (!imageUrl) {
              imageUrl = imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || '';
            }
          }
        }
      }

      if (url && title) {
        const fullUrl = url.startsWith('http') ? url
          : (url.startsWith('//') ? `https:${url}` : `https://${window.location.host}${url}`);

        if (imageUrl && !imageUrl.startsWith('http')) {
          imageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
        }

        products.push({
          url: fullUrl,
          title: title,
          featured_image: imageUrl || ''
        });
      }
    });

    // Remove duplicates
    return Array.from(new Map(products.map(p => [p.url, p])).values());
  }

  /**
   * Fetch recommended products from HTML
   */
  function fetchRecommendedProductsFromHTML(query, section) {
    if (isRenderingProducts) return;

    const isSearchBased = query && query.trim().length >= 2 && query !== '*';

    if (isSearchBased && (!query || query.trim().length < 2)) {
      if (!isRenderingProducts) {
        renderRecommendedProducts([]);
      }
      return;
    }

    if (!section) {
      if (!isRenderingProducts) {
        renderRecommendedProducts([]);
      }
      return;
    }

    const products = extractProductsFromHTML(section);

    if (isSearchBased && products.length === 0 && cachedRandomProducts && cachedRandomProducts.length > 0) {
      renderRecommendedProducts(cachedRandomProducts);
      return;
    }

    if (products.length === 0) {
      renderRecommendedProducts([]);
      return;
    }

    // Shuffle and limit to 6
    const shuffled = [...products].sort(() => Math.random() - 0.5);
    const limitedProducts = shuffled.slice(0, 6);
    renderRecommendedProducts(limitedProducts);
  }

  /**
   * Show loading spinner for products
   */
  function showProductsLoading() {
    if (!recommendedProductsGrid || !recommendedProductsContainer) return;

    recommendedProductsGrid.style.display = 'grid';
    recommendedProductsGrid.innerHTML = `
      <div class="recommended-products__loading" style="text-align: center; padding: 40px;">
        <div style="display: inline-block; width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid #333; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      </div>
    `;
  }

  /**
   * Render recommended products
   */
  function renderRecommendedProducts(products) {
    if (!recommendedProductsGrid || !recommendedProductsContainer) return;

    if (isRenderingProducts) return;
    isRenderingProducts = true;

    if (!products || products.length === 0) {
      recommendedProductsGrid.innerHTML = '';
      recommendedProductsGrid.style.display = 'none';
      isRenderingProducts = false;
      return;
    }

    recommendedProductsGrid.style.display = 'grid';

    // Cache random products when search is empty
    if (searchInput && !searchInput.value.trim() && products.length > 0) {
      cachedRandomProducts = products;
    }

    const html = products.map(product => {
      const url = product.url || '';
      const title = product.title || product.name || '';
      const imageUrl = product.featured_image || product.images?.[0] || '';
      const altText = title || 'Product image';

      let image = '';
      if (imageUrl) {
        if (imageUrl.startsWith('http') || imageUrl.includes('cdn.shopify.com')) {
          image = imageUrl;
        } else {
          image = typeof imageUrl === 'object'
            ? (imageUrl.src || imageUrl.url || '')
            : imageUrl;
        }
      }

      if (image && !image.startsWith('http')) {
        image = image.startsWith('//') ? `https:${image}` : image;
      }

      const escapedUrl = url.replace(/"/g, '&quot;');
      const escapedTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const escapedAlt = altText.replace(/"/g, '&quot;');
      const finalImage = image || config.fallbackImage;

      return `
        <a href="${escapedUrl}" class="recommended-products__item" title="${escapedTitle}">
          <div class="recommended-products__image">
            <img src="${finalImage}" alt="${escapedAlt}" class="recommended-products__img" loading="lazy">
          </div>
          <h3 class="recommended-products__name" title="${escapedTitle}">${escapedTitle}</h3>
        </a>
      `;
    }).join('');

    recommendedProductsGrid.innerHTML = html;
    isRenderingProducts = false;
  }

  /**
   * Fetch random recommended products
   */
  function fetchRandomRecommendedProducts() {
    if (currentProductsAbortController) {
      currentProductsAbortController.abort();
    }

    currentProductsAbortController = new AbortController();
    showProductsLoading();

    const htmlUrl = `${config.routesPredictiveSearchURL}?q=&resources[type]=product&resources[limit]=20&section_id=predictive-search`;
    const jsonUrl = `${config.routesPredictiveSearchURL}.json?q=&resources[type]=product&resources[limit]=20`;

    fetch(htmlUrl, { signal: currentProductsAbortController.signal })
      .then(res => {
        if (!res.ok) {
          return fetch(jsonUrl, {
            signal: currentProductsAbortController.signal,
            headers: { 'Accept': 'application/json' }
          }).then(res => res.ok ? res.json() : Promise.reject(new Error('Both endpoints failed')));
        }
        return res.text();
      })
      .then(data => {
        if (typeof data === 'string') {
          const doc = new DOMParser().parseFromString(data, 'text/html');
          const section = doc.querySelector('#shopify-section-predictive-search');

          if (section) {
            fetchRecommendedProductsFromHTML('*', section);
            return;
          } else {
            throw new Error('No products found in HTML');
          }
        } else {
          // Handle JSON response
          let products = [];

          if (data?.resources?.results?.products) {
            products = data.resources.results.products;
          } else if (data?.products) {
            products = data.products;
          }

          if (products.length === 0) {
            renderRecommendedProducts([]);
            return;
          }

          // Shuffle and pick 6 random products
          const shuffled = [...products].sort(() => Math.random() - 0.5);
          const limitedProducts = shuffled.slice(0, 6);

          const randomProducts = limitedProducts.map(product => {
            let imageUrl = '';

            if (product.images && product.images.length > 0) {
              imageUrl = typeof product.images[0] === 'string'
                ? product.images[0]
                : (product.images[0].src || product.images[0].url || '');
            } else if (product.image) {
              imageUrl = typeof product.image === 'string'
                ? product.image
                : (product.image.src || product.image.url || '');
            } else if (product.featured_image) {
              imageUrl = typeof product.featured_image === 'string'
                ? product.featured_image
                : (product.featured_image.src || product.featured_image.url || '');
            }

            if (imageUrl && !imageUrl.startsWith('http')) {
              imageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
            }

            let productUrl = product.url || '';
            if (!productUrl && product.handle) {
              productUrl = `/products/${product.handle}`;
            }

            return {
              url: productUrl,
              title: product.title || product.name || '',
              featured_image: imageUrl || ''
            };
          });

          renderRecommendedProducts(randomProducts);
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          // Try fallback
          const fallbackUrl = `${config.routesPredictiveSearchURL}.json?q=*&resources[type]=product&resources[limit]=20`;
          fetch(fallbackUrl, {
            signal: currentProductsAbortController.signal,
            headers: { 'Accept': 'application/json' }
          })
            .then(res => res.ok ? res.json() : null)
            .then(data => {
              let products = [];
              if (data?.resources?.results?.products) {
                products = data.resources.results.products;
              }

              if (products.length > 0) {
                const shuffled = [...products].sort(() => Math.random() - 0.5);
                const limitedProducts = shuffled.slice(0, 6);
                const randomProducts = limitedProducts.map(product => {
                  const imageUrl = product.featured_image?.url || product.featured_image || product.image || '';
                  return {
                    url: product.url || `/products/${product.handle || ''}`,
                    title: product.title || product.name || '',
                    featured_image: imageUrl || ''
                  };
                });
                renderRecommendedProducts(randomProducts);
              } else {
                renderRecommendedProducts([]);
              }
            })
            .catch(() => {
              renderRecommendedProducts([]);
            });
        }
      });
  }

  /**
   * Clear recommended products
   */
  function clearRecommendedProducts() {
    if (recommendedProductsGrid) {
      recommendedProductsGrid.innerHTML = '';
      recommendedProductsGrid.style.display = 'none';
    }
    if (currentProductsAbortController) {
      currentProductsAbortController.abort();
    }
  }

  /**
   * Setup slideout observer
   */
  function setupSlideoutObserver() {
    const searchSlideout = document.getElementById('slideout-search-sidebar');
    if (!searchSlideout) return;

    let isOpening = false;

    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isActive = searchSlideout.classList.contains('slideout--active');

          if (isActive && !isOpening) {
            isOpening = true;

            if (loadProductsTimeout) {
              clearTimeout(loadProductsTimeout);
            }

            loadProductsTimeout = setTimeout(() => {
              if (searchInput) {
                const query = searchInput.value.trim();
                if (query && query.length >= 2) {
                  fetchSearchSuggestions(query);
                } else {
                  if (cachedRandomProducts && !isRenderingProducts) {
                    renderRecommendedProducts(cachedRandomProducts);
                  } else if (!isRenderingProducts) {
                    fetchRandomRecommendedProducts();
                  }
                }
              }
              isOpening = false;
              loadProductsTimeout = null;
            }, 300);
          } else if (!isActive) {
            isOpening = false;
            clearRecommendedProducts();
          }
        }
      });
    });

    observer.observe(searchSlideout, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Check if slideout is already open on page load
    setTimeout(() => {
      if (searchSlideout.classList.contains('slideout--active') && !isRenderingProducts) {
        if (searchInput) {
          const query = searchInput.value.trim();
          if (query && query.length >= 2) {
            fetchSearchSuggestions(query);
          } else {
            if (cachedRandomProducts) {
              renderRecommendedProducts(cachedRandomProducts);
            } else {
              fetchRandomRecommendedProducts();
            }
          }
        }
      }
    }, 500);
  }

  /**
   * Setup event listeners
   */
  function setupEventListeners() {
    const debouncedFetch = debounce((q) => fetchSearchSuggestions(q), 250);

    searchInput.addEventListener('focus', function() {
      const query = this.value.trim();
      if (query && query.length >= 2) {
        debouncedFetch(query);
      } else {
        const hasProducts = recommendedProductsGrid.innerHTML.trim() &&
                          !recommendedProductsGrid.querySelector('.recommended-products__loading');

        if (!hasProducts && !isRenderingProducts) {
          if (cachedRandomProducts) {
            renderRecommendedProducts(cachedRandomProducts);
          } else {
            fetchRandomRecommendedProducts();
          }
        }
      }
    });

    searchInput.addEventListener('input', function() {
      toggleClearButton();
      const q = this.value.trim();
      if (q.length >= 2) {
        debouncedFetch(q);
      } else {
        searchSuggestions.innerHTML = '';
        hideSuggestions();

        if (cachedRandomProducts && !isRenderingProducts) {
          renderRecommendedProducts(cachedRandomProducts);
        } else if (!isRenderingProducts) {
          fetchRandomRecommendedProducts();
        }
      }
    });

    searchInput.addEventListener('keyup', function() {
      toggleClearButton();
    });

    searchInput.addEventListener('blur', function() {
      setTimeout(hideSuggestions, 200);
    });

    if (clearButton) {
      clearButton.addEventListener('click', function(e) {
        e.preventDefault();
        clearSearch();
        setTimeout(() => {
          if (cachedRandomProducts && !isRenderingProducts) {
            renderRecommendedProducts(cachedRandomProducts);
          } else if (!isRenderingProducts) {
            fetchRandomRecommendedProducts();
          }
        }, 100);
      });
    }

    searchInput.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') hideSuggestions();
      if (e.key === 'Escape') {
        hideSuggestions();
        this.blur();
      }
    });
  }

  /**
   * Initialize search functionality
   */
  function init() {
    initConfig();

    if (!initDOMElements()) {
      return;
    }

    setupSlideoutObserver();
    setupEventListeners();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

